import React, { useEffect, useState } from "react";
import api from "../api";
import {
  Plus, Pencil, Trash2, Code, Eye, X, Copy, Check, Save,
  ExternalLink, BarChart3, Power, PowerOff,
} from "lucide-react";

const DEFAULT_FORM = {
  name: "",
  title: "Scopri come sta il tuo sito web",
  subtitle: "Analisi gratuita e immediata della performance SEO e tecnica del tuo sito. Ricevi il report direttamente via email.",
  color: "#ea580c",
  buttonTextColor: "#ffffff",
  bgColor: "#ffffff",
  ctaText: "Analizza il mio sito",
  successMessage: "Grazie! Riceverai l'analisi via email entro pochi minuti.",
  redirectUrl: "",
  fields: {
    showFirstName: true, showLastName: true, showCompanyName: true, showPhone: true,
    showWhatYouOffer: true, showWhereYouWork: true,
    requireFirstName: true, requireLastName: false, requireCompanyName: false, requirePhone: false,
    requireWhatYouOffer: false, requireWhereYouWork: false,
  },
  labels: {
    firstName: "", lastName: "", companyName: "", email: "", phone: "", website: "",
    whatYouOffer: "", whereYouWork: "",
  },
  requirePrivacy: true,
  privacyText: "Accetto l'informativa sulla privacy e autorizzo il trattamento dei miei dati per ricevere l'analisi del sito.",
  privacyUrl: "https://www.privacylab.it/archive.php?id=3042797&idDoc=51&idTarget=444217&output=html",
  isActive: true,
};

const esc = (s) => String(s || "").replace(/[&<>"']/g, c => ({
  "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"
})[c]);

/** Render a STATIC visual mock of the form for the preview iframe.
 * Matches the look produced by widget.js, but without any live fetch. */
function mockFormHtml(f) {
  const fields = f.fields || DEFAULT_FORM.fields;
  const labels = f.labels || {};
  const color = esc(f.color || "#ea580c");
  const bgColor = esc(f.bgColor || "#ffffff");
  const btnTextColor = esc(f.buttonTextColor || "#ffffff");
  const inputStyle = "width:100%;padding:10px 12px;border:1px solid #cbd5e1;border-radius:6px;font-size:14px;font-family:system-ui;box-sizing:border-box;background:white";
  const lblStyle = "display:block;font-size:13px;font-weight:600;color:#334155;margin-bottom:4px;font-family:system-ui";
  const lab = (k, fallback) => esc(labels[k] || fallback);
  const field = (k, fallback, req) => {
    const placeholder = lab(k, fallback);
    return `<div style="margin-bottom:12px"><label style="${lblStyle}">${placeholder}${req?" *":""}</label><input type="text" placeholder="${placeholder}" style="${inputStyle}" tabindex="-1" readonly/></div>`;
  };
  const showNamePair = fields.showFirstName || fields.showLastName;
  let privacy = "";
  if (f.requirePrivacy !== false) {
    const text = esc(f.privacyText || "Accetto l'informativa sulla privacy.");
    const url = f.privacyUrl || "";
    let html = text;
    if (url) {
      html = html.replace(/(privacy)/i,
        `<a href="${esc(url)}" target="_blank" rel="noopener" style="color:${color};text-decoration:underline">$1</a>`);
      if (html === text) html = text + ` <a href="${esc(url)}" target="_blank" rel="noopener" style="color:${color};text-decoration:underline">(leggi)</a>`;
    }
    privacy = `<div style="margin:8px 0 14px;display:flex;align-items:flex-start;gap:8px"><input type="checkbox" style="margin-top:3px;width:16px;height:16px;accent-color:${color}" tabindex="-1"/><span style="font-size:12px;color:#475569;line-height:1.5">${html} <span style="color:#b91c1c">*</span></span></div>`;
  }
  return `<!doctype html><html><head><meta charset="utf-8"/></head>
<body style="margin:0;padding:24px;background:#f1f5f9;font-family:system-ui">
  <div style="background:${bgColor};border:1px solid #e2e8f0;border-radius:12px;padding:24px;max-width:520px;margin:0 auto;box-shadow:0 2px 8px rgba(0,0,0,0.04)">
    <div style="font-size:22px;font-weight:800;color:${color};margin-bottom:6px;line-height:1.25">${esc(f.title)}</div>
    <p style="font-size:14px;color:#64748b;margin:0 0 18px;line-height:1.5">${esc(f.subtitle)}</p>
    ${showNamePair ? `<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
      ${fields.showFirstName ? field("firstName","Nome", fields.requireFirstName) : ""}
      ${fields.showLastName ? field("lastName","Cognome", fields.requireLastName) : ""}
    </div>` : ""}
    ${fields.showCompanyName ? field("companyName","Azienda", fields.requireCompanyName) : ""}
    ${field("email","Email", true)}
    ${fields.showPhone ? field("phone","Telefono", fields.requirePhone) : ""}
    ${field("website","Sito web da analizzare", true)}
    ${fields.showWhatYouOffer ? field("whatYouOffer","Cosa offri? (3-5 parole, es. commercialista per aziende)", fields.requireWhatYouOffer) : ""}
    ${fields.showWhereYouWork ? field("whereYouWork","Dove lavori? (città/provincia oppure tutta Italia)", fields.requireWhereYouWork) : ""}
    ${privacy}
    <button style="width:100%;background:${color};color:${btnTextColor};border:0;padding:13px 16px;border-radius:8px;font-size:15px;font-weight:700;font-family:system-ui;cursor:pointer">${esc(f.ctaText)}</button>
    <p style="text-align:center;font-size:11px;color:#94a3b8;margin:14px 0 0">Sicuro · GDPR-friendly · Powered by MaxValle</p>
  </div>
</body></html>`;
}


export default function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | {id, ...}
  const [embedFor, setEmbedFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [copyState, setCopyState] = useState("");
  // Use the SAME domain where the admin is currently logged in — that way the
  // embed code matches the user's chosen domain (e.g. custom domain on prod,
  // emergent preview on staging). REACT_APP_BACKEND_URL is reserved for
  // internal API calls and may differ from the public-facing domain.
  const baseUrl = (typeof window !== "undefined" && window.location.origin)
    || process.env.REACT_APP_BACKEND_URL || "";

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forms");
      setForms(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => { setEditing("new"); setForm(DEFAULT_FORM); };

  const startEdit = (f) => {
    setEditing(f);
    setForm({
      name: f.name, title: f.title, subtitle: f.subtitle,
      color: f.color,
      buttonTextColor: f.buttonTextColor || "#ffffff",
      bgColor: f.bgColor || "#ffffff",
      ctaText: f.ctaText,
      successMessage: f.successMessage,
      redirectUrl: f.redirectUrl || "",
      fields: f.fields || DEFAULT_FORM.fields,
      labels: f.labels || DEFAULT_FORM.labels,
      requirePrivacy: f.requirePrivacy !== false,
      privacyText: f.privacyText || DEFAULT_FORM.privacyText,
      privacyUrl: f.privacyUrl || "",
      isActive: f.isActive !== false,
    });
  };

  const save = async () => {
    if (!form.name.trim()) { alert("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form, redirectUrl: form.redirectUrl?.trim() || null };
      if (editing === "new") await api.post("/forms", payload);
      else await api.put(`/forms/${editing.id}`, payload);
      setEditing(null);
      await reload();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Eliminare il form "${f.name}"? Lo snippet già installato sui siti smetterà di funzionare.`)) return;
    await api.delete(`/forms/${f.id}`);
    await reload();
  };

  const toggleActive = async (f) => {
    await api.put(`/forms/${f.id}`, {
      ...f, isActive: !f.isActive,
      redirectUrl: f.redirectUrl || null,
    });
    await reload();
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopyState(key);
    setTimeout(() => setCopyState(""), 1800);
  };

  const embedCode = (slug) =>
    `<!-- MaxValle Lead Form: ${slug} -->\n` +
    `<div data-mvform="${slug}"></div>\n` +
    `<script src="${baseUrl}/api/forms/${slug}/widget.js" async></script>`;

  return (
    <div data-testid="form-builder-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold">Form Builder</h1>
          <p className="text-small text-muted" style={{marginTop:4, maxWidth:700, lineHeight:1.5}}>
            Crea form personalizzati da incorporare su qualunque landing page o sito WordPress.
            Le richieste finiscono direttamente in <strong>Lead Inbound</strong> e attivano l'analisi
            automatica del sito.
          </p>
        </div>
        <button className="btn btn-primary" onClick={startNew} data-testid="new-form-btn">
          <Plus size={15}/> Nuovo form
        </button>
      </div>

      {loading ? (
        <div><span className="spinner"/></div>
      ) : forms.length === 0 ? (
        <div className="card text-center" style={{padding:60}}>
          <div style={{fontSize:48, marginBottom:8, color:"var(--mv-text-muted)"}}>📝</div>
          <div className="text-muted">Nessun form creato. Clicca "Nuovo form" per iniziare.</div>
        </div>
      ) : (
        <div className="card" style={{padding:0,overflow:"auto"}}>
          <table className="tbl" data-testid="forms-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Slug</th>
                <th>Stato</th>
                <th>Impressions</th>
                <th title="Richieste valide uniche (no bot, no duplicati) / Submission totali ricevute">Richieste uniche</th>
                <th>Analizzati</th>
                <th>Conv. Rate</th>
                <th>Bloccati</th>
                <th style={{width:160, textAlign:"right"}}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {forms.map(f => {
                const s = f.stats || {};
                const totalSubs = s.submissions || 0;
                const blocked = (s.honeypot_blocked || 0) + (s.duplicate_blocked || 0);
                const uniqueValid = Math.max(0, totalSubs - blocked);
                const conv = uniqueValid ? Math.min(100, Math.round((s.successful_analyses / uniqueValid) * 100)) : 0;
                return (
                  <tr key={f.id} data-testid={`form-row-${f.id}`}>
                    <td>
                      <div style={{display:"flex", alignItems:"center", gap:8}}>
                        <span style={{
                          width:10, height:10, borderRadius:"50%",
                          background: f.color, flexShrink:0,
                        }}/>
                        <div>
                          <div style={{fontWeight:700}}>{f.name}</div>
                          <div className="text-small text-muted" style={{
                            maxWidth:280, overflow:"hidden", textOverflow:"ellipsis",
                            whiteSpace:"nowrap"
                          }}>{f.title}</div>
                        </div>
                      </div>
                    </td>
                    <td><code style={{fontSize:11, color:"var(--mv-text-muted)"}}>{f.slug}</code></td>
                    <td>
                      {f.isActive ? (
                        <span className="badge badge-green">● Attivo</span>
                      ) : (
                        <span className="badge badge-gray">○ Disattivato</span>
                      )}
                    </td>
                    <td><strong>{s.impressions || 0}</strong></td>
                    <td title="Richieste valide uniche / Submission totali (incluse duplicate e bot)">
                      <strong style={{color: uniqueValid ? "var(--mv-text)" : "var(--mv-text-muted)"}} data-testid={`unique-count-${f.id}`}>{uniqueValid}</strong>
                      <span className="text-small text-muted"> / {totalSubs}</span>
                    </td>
                    <td style={{color:"var(--mv-success)", fontWeight:700}}>{s.successful_analyses || 0}</td>
                    <td>
                      <span style={{fontWeight:600, color: conv >= 50 ? "var(--mv-success)" : conv >= 20 ? "#b45309" : "var(--mv-text-muted)"}}>
                        {uniqueValid ? `${conv}%` : "—"}
                      </span>
                    </td>
                    <td className="text-small text-muted">
                      {s.honeypot_blocked || 0} bot · {s.duplicate_blocked || 0} dup
                    </td>
                    <td style={{textAlign:"right", whiteSpace:"nowrap"}}>
                      <button className="btn-icon" title="Anteprima"
                        onClick={()=>setPreviewFor(f)} data-testid={`preview-btn-${f.id}`}>
                        <Eye size={14}/>
                      </button>
                      <button className="btn-icon" title="Codice embed"
                        onClick={()=>setEmbedFor(f)} data-testid={`embed-btn-${f.id}`}>
                        <Code size={14}/>
                      </button>
                      <button className="btn-icon" title={f.isActive ? "Disattiva" : "Attiva"}
                        onClick={()=>toggleActive(f)}>
                        {f.isActive ? <PowerOff size={14}/> : <Power size={14}/>}
                      </button>
                      <button className="btn-icon" title="Modifica"
                        onClick={()=>startEdit(f)} data-testid={`edit-btn-${f.id}`}>
                        <Pencil size={14}/>
                      </button>
                      <button className="btn-icon" title="Elimina"
                        onClick={()=>remove(f)} data-testid={`delete-btn-${f.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- Edit modal ---------- */}
      {editing && (
        <div className="modal-backdrop" onClick={()=>setEditing(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} data-testid="form-edit-modal">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="h-section">{editing === "new" ? "Nuovo form" : `Modifica: ${editing.name}`}</div>
                <div className="text-small text-muted">Tutte le modifiche sono immediate sui siti dove è installato.</div>
              </div>
              <button className="btn-icon" onClick={()=>setEditing(null)}><X size={16}/></button>
            </div>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14}}>
              <div>
                <label className="label">Nome interno *</label>
                <input className="input" value={form.name}
                  onChange={e=>setForm({...form, name:e.target.value})}
                  placeholder='Es. "Landing dentisti — homepage"'
                  data-testid="form-name"/>
                <div className="text-small text-muted" style={{marginTop:4}}>
                  Per distinguere i form. Non visibile al visitatore.
                </div>
              </div>
              <div>
                <label className="label">Colore principale</label>
                <div style={{display:"flex", gap:8, alignItems:"center"}}>
                  <input type="color" value={form.color}
                    onChange={e=>setForm({...form, color:e.target.value})}
                    style={{width:50, height:38, border:"1px solid var(--mv-border)", borderRadius:6, cursor:"pointer", padding:2}}
                    data-testid="form-color"/>
                  <input className="input" value={form.color}
                    onChange={e=>setForm({...form, color:e.target.value})}
                    placeholder="#ea580c" style={{flex:1, fontFamily:"monospace"}}/>
                </div>
              </div>
            </div>

            <label className="label">Titolo</label>
            <input className="input" value={form.title}
              onChange={e=>setForm({...form, title:e.target.value})}
              data-testid="form-title"/>

            <label className="label mt-3">Sottotitolo</label>
            <textarea className="input" rows={2} value={form.subtitle}
              onChange={e=>setForm({...form, subtitle:e.target.value})}/>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:12}}>
              <div>
                <label className="label">Testo del bottone</label>
                <input className="input" value={form.ctaText}
                  onChange={e=>setForm({...form, ctaText:e.target.value})}/>
              </div>
              <div>
                <label className="label">Redirect URL post-submit (opzionale)</label>
                <input className="input" value={form.redirectUrl}
                  onChange={e=>setForm({...form, redirectUrl:e.target.value})}
                  placeholder="https://esempio.it/grazie"/>
              </div>
            </div>

            <label className="label mt-3">Messaggio di ringraziamento (mostrato se non c'è redirect)</label>
            <textarea className="input" rows={2} value={form.successMessage}
              onChange={e=>setForm({...form, successMessage:e.target.value})}/>

            <div className="card mt-3" style={{padding:12, background:"var(--mv-bg-soft)"}}>
              <div style={{fontWeight:600, marginBottom:10, fontSize:13}}>Campi del form</div>
              <div className="text-small text-muted" style={{marginBottom:10}}>
                <strong>Sito web</strong> e <strong>Email</strong> sono sempre presenti e obbligatori.
              </div>
              {[
                { key:"FirstName", label:"Nome", lk:"firstName", ph:"Nome" },
                { key:"LastName",  label:"Cognome", lk:"lastName", ph:"Cognome" },
                { key:"CompanyName", label:"Azienda", lk:"companyName", ph:"Azienda" },
                { key:"Phone",     label:"Telefono", lk:"phone", ph:"Telefono" },
                { key:"WhatYouOffer", label:"Cosa offri", lk:"whatYouOffer",
                  ph:"Cosa offri? (3-5 parole, es. commercialista per aziende)",
                  hint:"Serve al report per settore, concorrenti e domanda di mercato" },
                { key:"WhereYouWork", label:"Dove lavori", lk:"whereYouWork",
                  ph:"Dove lavori? (città/provincia oppure tutta Italia)",
                  hint:"Serve per confrontare solo i concorrenti della tua zona" },
              ].map(({key, label, lk, ph, hint}) => (
                <div key={key} style={{padding:"8px 0", borderBottom:"1px solid var(--mv-border-light)"}}>
                  <div className="flex justify-between items-center">
                    <span style={{fontSize:13}}>{label}</span>
                    <div className="flex gap-3 items-center text-small">
                      <label style={{cursor:"pointer"}}>
                        <input type="checkbox" checked={!!form.fields[`show${key}`]}
                          data-testid={`field-show-${lk}`}
                          onChange={e=>setForm({...form, fields:{...form.fields, [`show${key}`]: e.target.checked}})}/>
                        <span style={{marginLeft:4}}>Mostra</span>
                      </label>
                      <label style={{cursor: form.fields[`show${key}`] ? "pointer" : "not-allowed", opacity: form.fields[`show${key}`] ? 1 : 0.4}}>
                        <input type="checkbox" checked={!!form.fields[`require${key}`]}
                          disabled={!form.fields[`show${key}`]}
                          data-testid={`field-require-${lk}`}
                          onChange={e=>setForm({...form, fields:{...form.fields, [`require${key}`]: e.target.checked}})}/>
                        <span style={{marginLeft:4}}>Obbligatorio</span>
                      </label>
                    </div>
                  </div>
                  {form.fields[`show${key}`] && (
                    <>
                      <input className="input mt-1" style={{fontSize:12.5,padding:"6px 9px"}}
                        data-testid={`field-label-${lk}`} placeholder={`Etichetta: ${ph}`}
                        value={(form.labels || {})[lk] || ""}
                        onChange={e=>setForm({...form, labels:{...(form.labels||{}), [lk]: e.target.value}})}/>
                      {hint && <div className="text-small text-muted" style={{marginTop:3,fontSize:11.5}}>{hint}</div>}
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2 justify-end mt-4">
              <button className="btn btn-secondary" onClick={()=>setEditing(null)} disabled={saving}>Annulla</button>
              <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="form-save">
                <Save size={14}/> {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Embed modal ---------- */}
      {embedFor && (
        <div className="modal-backdrop" onClick={()=>setEmbedFor(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} data-testid="embed-modal">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="h-section">Codice embed — {embedFor.name}</div>
                <div className="text-small text-muted">Incolla questo codice nella pagina del tuo sito (HTML, WordPress, Wix, ecc.).</div>
              </div>
              <button className="btn-icon" onClick={()=>setEmbedFor(null)}><X size={16}/></button>
            </div>

            <div className="text-small" style={{fontWeight:600, marginBottom:6}}>
              1. Codice da incollare
            </div>
            <pre style={{
              background:"#0f172a", color:"#e2e8f0", padding:14, borderRadius:8,
              fontSize:12, fontFamily:"monospace", overflow:"auto",
              lineHeight:1.6,
            }} data-testid="embed-code">{embedCode(embedFor.slug)}</pre>
            <button className="btn btn-primary mt-2" onClick={()=>copy(embedCode(embedFor.slug), "code")}
              data-testid="copy-embed-btn">
              {copyState === "code" ? <><Check size={14}/> Copiato!</> : <><Copy size={14}/> Copia codice</>}
            </button>

            <div className="text-small mt-4" style={{fontWeight:600, marginBottom:6}}>
              2. Anteprima
            </div>
            <a href={`${baseUrl}/api/forms/${embedFor.slug}/config`} target="_blank" rel="noreferrer"
              className="text-small" style={{color:"var(--mv-primary)"}}>
              <ExternalLink size={12} style={{display:"inline", verticalAlign:"middle"}}/> Verifica config pubblica
            </a>

            <div className="card mt-4" style={{padding:14, background:"#fef3c7", borderLeft:"4px solid #f59e0b"}}>
              <div className="text-small" style={{lineHeight:1.6}}>
                <strong>WordPress:</strong> incolla nella sezione "HTML personalizzato" del block editor o dentro un widget HTML.<br/>
                <strong>Anti-spam:</strong> il form ha un campo honeypot invisibile che blocca i bot automaticamente.<br/>
                <strong>Anti-duplicati:</strong> la stessa email non può richiedere l'analisi <em>dello stesso sito</em> più di una volta ogni 30 giorni (può però analizzare siti diversi).
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Preview modal ---------- */}
      {previewFor && (
        <div className="modal-backdrop" onClick={()=>setPreviewFor(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{maxWidth:680}}>
            <div className="flex justify-between items-start mb-3">
              <div className="h-section">Anteprima — {previewFor.name}</div>
              <button className="btn-icon" onClick={()=>setPreviewFor(null)}><X size={16}/></button>
            </div>
            <div style={{background:"#f1f5f9", padding:20, borderRadius:8}}>
              <iframe
                title="form-preview"
                srcDoc={mockFormHtml(previewFor)}
                style={{width:"100%", height:560, border:"none", borderRadius:8, background:"#f1f5f9"}}
                data-testid="preview-iframe"/>
            </div>
            <div className="text-small text-muted mt-2">
              Questa è un'anteprima visiva (non interattiva). Per testare il flusso completo,
              copia il codice embed e incollalo su un sito reale.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
